import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  TextField
} from '@mui/material';
import { forgotPassword } from '../../services/userService';
import { Utils } from '../../utils/utils';

export default function ForgotPassword(props) {
  const [requesting, setRequesting] = useState(false);
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState('');
  const [serviceError, setServiceError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!props.open) {
      return;
    }

    const initialEmail = props.email?.trim() || '';
    setEmail(initialEmail);

    if (!initialEmail) {
      setValidationError('Email is required to request a password change.');
    } else if (!Utils.validateEmailFormat(initialEmail)) {
      setValidationError('Please enter a valid email address');
    } else {
      setValidationError('');
    }
    setServiceError('');
    setSuccessMessage('');
  }, [props.open, props.email]);

  const handleEmailChange = (event) => {
    const typedEmail = event.target.value;
    setEmail(typedEmail);

    if (!typedEmail.trim()) {
      setValidationError('Email is required to request a password change.');
    } else if (!Utils.validateEmailFormat(typedEmail)) {
      setValidationError('Please enter a valid email address');
    } else {
      setValidationError('');
    }

    setServiceError('');
    setSuccessMessage('');
  };

  const handleRequestPasswordChange = async () => {
    if (validationError) {
      return;
    }

    setRequesting(true);
    setServiceError('');
    setSuccessMessage('');

    if (props.onRequestPasswordChange) {
      await props.onRequestPasswordChange(email.trim());
    }

    forgotPassword(email.trim()).then(() => {
      setRequesting(false);
      setSuccessMessage('Password reset link has been sent.');
    }).catch(() => {
      setRequesting(false);
      setServiceError('Sorry, an error occurred while requesting password reset. Please try again.');
    });
  };

  const handleClose = () => {
    setValidationError('');
    setServiceError('');
    setSuccessMessage('');
    if (props.onClose) {
      props.onClose();
    }
  };

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="sm" data-testid="forgot-password-dialog">
      <DialogTitle>Forgot Password</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please enter the email associated with your account. You will receive a link to reset your password. Click "Request Password Change" to proceed.
        </Typography>

        <TextField
          fullWidth
          margin="normal"
          label="Email"
          name="email"
          value={email}
          onChange={handleEmailChange}
          error={!!validationError}
          helperText={validationError}
          inputProps={{ 'data-testid': 'forgot-password-email-input' }}
        />

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="forgot-password-success-alert">
            {successMessage}
          </Alert>
        )}

        {serviceError && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="forgot-password-service-error-alert">
            {serviceError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }} data-testid="forgot-password-close-button">
          Close
        </Button>
        {!successMessage && (
          <Button
            variant="contained"
            onClick={handleRequestPasswordChange}
            disabled={requesting || !!validationError}
            sx={{ textTransform: 'none' }}
            data-testid="forgot-password-request-button"
          >
            {requesting ? 'Requesting...' : 'Request Password Change'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
